import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Users, Trophy, TrendingUp, User } from 'lucide-react';
import AnalyticsListModal from '../modals/AnalyticsListModal';

interface AnalyticsStaffProductsProps {
    staffData: any[];
    servicesData: any[];
    onOpenListModal: (type: 'staff' | 'services') => void;
}

const AnalyticsStaffProducts: React.FC<AnalyticsStaffProductsProps> = ({ staffData, servicesData, onOpenListModal }) => {

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    const EmptyState = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm">
                <Icon size={20} className="text-gray-300" />
            </div>
            <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            <p className="text-xs text-gray-400 mt-1 max-w-[200px]">{desc}</p>
        </div>
    );

    const EmptySlotCard = () => (
        <div className="flex items-center justify-between p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50 opacity-60">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full" />
                <div className="space-y-2">
                    <div className="w-24 h-3 bg-gray-200 rounded" />
                    <div className="w-16 h-2 bg-gray-200 rounded" />
                </div>
            </div>
            <div className="w-16 h-4 bg-gray-200 rounded" />
        </div>
    );

    const topPerformer = staffData.length > 0 ? staffData[0] : null;
    const otherStaffPreview = staffData.length > 1 ? staffData.slice(1, 4) : [];

    const slotsFilled = Math.min(staffData.length, 4);
    const placeholderCount = Math.max(0, 3 - (slotsFilled === 1 ? 0 : slotsFilled - 1));
    const placeholders = Array(placeholderCount).fill(0);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* --- STAFF PERFORMANCE --- */}
            <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex flex-col">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Performance Staff</h3>
                        <p className="text-sm text-gray-400 mt-1">Classifica fatturato</p>
                    </div>
                    {staffData.length > 0 ? (
                        <button
                            onClick={() => onOpenListModal('staff')}
                            className="text-xs font-bold text-clip-600 hover:text-clip-700 bg-clip-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                            Vedi Tutto
                        </button>
                    ) : (
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <Users size={20} className="text-gray-400" />
                        </div>
                    )}
                </div>

                <div className="flex-1 flex flex-col gap-3">
                    {staffData.length > 0 ? (
                        <>
                            {/* 1. TOP PERFORMER */}
                            {topPerformer && (
                                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-orange-100 flex items-center justify-between shadow-sm relative overflow-hidden group">
                                    <div className="absolute right-0 top-0 p-4 opacity-10 transform translate-x-2 -translate-y-2 group-hover:scale-110 transition-transform">
                                        <Trophy size={40} className="text-amber-600" />
                                    </div>

                                    <div className="flex items-center gap-3 relative z-10">
                                        <div className="w-10 h-10 bg-white text-amber-500 rounded-full flex items-center justify-center shadow-sm border border-amber-100">
                                            <Trophy size={18} />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-gray-900 text-sm">{topPerformer.name}</p>
                                                <span className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Top</span>
                                            </div>
                                            <p className="text-xs text-amber-600/80 font-medium">Ottimo lavoro!</p>
                                        </div>
                                    </div>
                                    <div className="text-right relative z-10">
                                        <p className="text-lg font-bold text-gray-900">€ {topPerformer.revenue.toLocaleString()}</p>
                                    </div>
                                </div>
                            )}

                            {/* 2. ALTRI MEMBRI */}
                            {otherStaffPreview.map((staff, idx) => (
                                <div key={idx} className="p-4 bg-white rounded-xl border border-gray-100 flex items-center justify-between hover:border-gray-300 hover:shadow-sm transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center">
                                            <User size={18} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-700 text-sm">{staff.name}</p>
                                            <p className="text-xs text-gray-400">Collaboratore</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-gray-700">€ {staff.revenue.toLocaleString()}</p>
                                    </div>
                                </div>
                            ))}

                            {/* 3. PLACEHOLDERS */}
                            {placeholders.map((_, i) => (
                                <EmptySlotCard key={`empty-${i}`} />
                            ))}
                        </>
                    ) : (
                        <EmptyState
                            icon={Users}
                            title="Nessuna performance"
                            desc="Non ci sono dati di vendita associati allo staff nel periodo."
                        />
                    )}
                </div>
            </motion.div>

            {/* --- TOP SERVIZI --- */}
            <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Top Servizi</h3>
                        <p className="text-sm text-gray-400 mt-1">I servizi più richiesti</p>
                    </div>
                    {servicesData.length > 0 && (
                        <button
                            onClick={() => onOpenListModal('services')}
                            className="text-xs font-bold text-clip-600 hover:text-clip-700 bg-clip-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                            Vedi Tutto
                        </button>
                    )}
                </div>

                <div className="flex-1 flex flex-col gap-3">
                    {servicesData.length > 0 ? (
                        <div className="space-y-4">
                            {servicesData.slice(0, 4).map((service, i) => (
                                <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer border border-transparent hover:border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${i === 0 ? 'bg-amber-50 text-amber-500' : 'bg-gray-100 text-gray-400 group-hover:bg-clip-50 group-hover:text-clip-500'}`}>
                                            {i === 0 ? <Trophy size={18} /> : <ShoppingBag size={18} />}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 text-sm truncate max-w-[150px]">{service.name}</h4>
                                            <p className="text-xs text-gray-400">{service.rawValue} eseguiti</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="block font-bold text-gray-900">{service.value}%</span>
                                        {i === 0 && <span className="text-[10px] text-green-600 flex items-center justify-end gap-0.5"><TrendingUp size={10} /> Top</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={ShoppingBag}
                            title="Nessun servizio top"
                            desc="Non ci sono abbastanza dati per determinare i servizi più popolari."
                        />
                    )}
                </div>
            </motion.div>

        </div>
    );
};

export default AnalyticsStaffProducts;